import { ExpandLess as ExpandLessIcon, ExpandMore as ExpandMoreIcon } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import { type DateTime } from "luxon";

import { type MeetingTimeSummaryProps } from "../../organisms/AddEventTimesModal/AddEventTimesModal.types";
import {
  formatCapacityText,
  formatDateTimeForSummary,
} from "../../organisms/AddEventTimesModal/AddEventTimesModal.utils";

export const MeetingTimeSummary = ({
  dateFieldName,
  timeRangeFieldName,
  capacityFieldName,
  slotId,
  isExpanded,
  onToggleExpansion,
  formValues,
}: MeetingTimeSummaryProps) => {
  const date = formValues[dateFieldName] as DateTime | null;
  const timeRange = formValues[timeRangeFieldName] as [DateTime | null, DateTime | null] | null;
  const capacity = formValues[capacityFieldName];

  // Check if complete but don't auto-collapse from here
  // Auto-collapse will only happen when user adds another meeting time
  const isComplete = date && timeRange && timeRange[0] && timeRange[1] && capacity !== undefined;

  if (!isComplete) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic", mb: 2 }}>
        Select date and time to see summary
      </Typography>
    );
  }

  // Ensure we have valid Luxon DateTime objects before calling toFormat
  if (!date?.toFormat || !timeRange[0]?.toFormat || !timeRange[1]?.toFormat) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic", mb: 2 }}>
        Loading date and time...
      </Typography>
    );
  }

  const dateTimeText = formatDateTimeForSummary(date, timeRange);
  const capacityText = formatCapacityText(capacity);

  return (
    <Box
      onClick={() => onToggleExpansion(slotId)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onToggleExpansion(slotId);
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={`${isExpanded ? "Collapse" : "Expand"} meeting time details`}
      sx={{
        "&:hover": {
          backgroundColor: "primary.dark",
        },
        alignItems: "center",
        backgroundColor: "primary.main",
        borderRadius: 1,
        color: "primary.contrastText",
        cursor: "pointer",
        display: "flex",
        justifyContent: "space-between",
        mb: 2,
        p: 2,
        transition: "all 0.2s",
      }}
    >
      <Typography variant="body1" sx={{ fontWeight: "medium" }}>
        📅 {dateTimeText}
      </Typography>
      <Box sx={{ alignItems: "center", display: "flex", gap: 1 }}>
        <Typography variant="body1" sx={{ fontWeight: "medium" }}>
          ({capacityText})
        </Typography>
        {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </Box>
    </Box>
  );
};
